import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { LanguageProvider, useLanguage } from "./context/LanguageContext";
import Header from "./components/layout/Header";
import Footer from "./components/layout/Footer";
import MobileCTA from "./components/layout/MobileCTA";
import HomePage from "./pages/HomePage";
import AirportPage from "./pages/AirportPage";
import ServicesPage from "./pages/ServicesPage";
import FaresPage from "./pages/FaresPage";
import AreasPage from "./pages/AreasPage";
import FAQPage from "./pages/FAQPage";
import ContactPage from "./pages/ContactPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import DisclaimerPage from "./pages/DisclaimerPage";

// Redirect component for root path
const RootRedirect = () => {
  const { language, initialized } = useLanguage();
  const location = useLocation();
  
  if (!initialized) {
    return <div className="min-h-screen bg-background" />;
  }
  
  return <Navigate to={`/${language}`} replace />;
};

// Sync language from URL
const LanguageSync = ({ children }) => {
  const { language, setLanguage, initialized } = useLanguage();
  const location = useLocation();
  
  React.useEffect(() => {
    if (!initialized) return;
    
    // Sync language from URL
    if (location.pathname.startsWith('/fr') && language !== 'fr') {
      setLanguage('fr');
    } else if (location.pathname.startsWith('/en') && language !== 'en') {
      setLanguage('en');
    }
  }, [location.pathname, language, setLanguage, initialized]);
  
  return children;
};

// Language-aware routes wrapper
const AppRoutes = () => {
  return (
    <Routes>
      {/* Root redirect to language-prefixed home */}
      <Route path="/" element={<RootRedirect />} />
      
      {/* French Routes */}
      <Route path="/fr" element={<HomePage />} />
      <Route path="/fr/aeroport-yul" element={<AirportPage />} />
      <Route path="/fr/services" element={<ServicesPage />} />
      <Route path="/fr/tarifs" element={<FaresPage />} />
      <Route path="/fr/zones-desservies" element={<AreasPage />} />
      <Route path="/fr/faq" element={<FAQPage />} />
      <Route path="/fr/contact" element={<ContactPage />} />
      <Route path="/fr/confidentialite" element={<PrivacyPage />} />
      <Route path="/fr/conditions-utilisation" element={<TermsPage />} />
      <Route path="/fr/avertissement" element={<DisclaimerPage />} />
      
      {/* English Routes */}
      <Route path="/en" element={<HomePage />} />
      <Route path="/en/yul-airport" element={<AirportPage />} />
      <Route path="/en/services" element={<ServicesPage />} />
      <Route path="/en/fares" element={<FaresPage />} />
      <Route path="/en/service-areas" element={<AreasPage />} />
      <Route path="/en/faq" element={<FAQPage />} />
      <Route path="/en/contact" element={<ContactPage />} />
      <Route path="/en/privacy-policy" element={<PrivacyPage />} />
      <Route path="/en/terms-of-use" element={<TermsPage />} />
      <Route path="/en/disclaimer" element={<DisclaimerPage />} />
      
      {/* Legacy routes redirect to French */}
      <Route path="/airport" element={<Navigate to="/fr/aeroport-yul" replace />} />
      <Route path="/services" element={<Navigate to="/fr/services" replace />} />
      <Route path="/fares" element={<Navigate to="/fr/tarifs" replace />} />
      <Route path="/areas" element={<Navigate to="/fr/zones-desservies" replace />} />
      <Route path="/faq" element={<Navigate to="/fr/faq" replace />} />
      <Route path="/contact" element={<Navigate to="/fr/contact" replace />} />
      <Route path="/privacy" element={<Navigate to="/fr/confidentialite" replace />} />
      <Route path="/terms" element={<Navigate to="/fr/conditions-utilisation" replace />} />
      <Route path="/disclaimer" element={<Navigate to="/fr/avertissement" replace />} />
      
      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/fr" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <LanguageProvider>
      <div className="App min-h-screen bg-background">
        <BrowserRouter>
          <LanguageSync>
            <Header />
            <main className="pb-20 md:pb-0">
              <AppRoutes />
            </main>
            <Footer />
            <MobileCTA />
          </LanguageSync>
        </BrowserRouter>
      </div>
    </LanguageProvider>
  );
}

export default App;
